import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from './MainHeader/Header';
import { bidJob, fetchAvailableJobs, fetchJourneyDetails, getAllCars, checkBidJobs, updateUserSeen, checkUserToken } from '../api';
import JobsTabs from './JobsTabs';
import Loading from './Loading/Loading';
import QuotationCardSkeleton from './Loading/QuotationCardSkeleton';
import { DateTime, Duration } from 'luxon';
import Select from 'react-select';
import { useJobsCounts } from './JobsCountsProvider';


export default function AvailableJob() {
    const location = useLocation();
    const user = location.state?.user || JSON.parse(localStorage.getItem('user'));
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [activeItem, setActiveItem] = useState('dashboard');
    const [jobs, setJobs] = useState([]);
    const [filteredJobs, setFilteredJobs] = useState([]);
    const [actionLoading, setActionLoading] = useState(false);
    const { refreshCounts } = useJobsCounts();
    const [filters, setFilters] = useState({
        booking_ref_id: '',
        from_address: '',
        to_address: '',
        pickup_date: '',
        car_id: [],
    });
    const [page, setPage] = useState(1);
    const [perPage] = useState(5);
    const [pagination, setPagination] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [selectedJob, setSelectedJob] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [loading, setLoading] = useState(false);
    const [detailsError, setDetailsError] = useState('');
    const [isChecked, setIsChecked] = useState(false);
    const [quote, setQuote] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [reaction, setReaction] = useState(false);
    const [isExpired, setIsExpired] = useState(false);
    const [disabledButton, setDisabledButton] = useState(new Set());
const validateUserToken = async () => {
    if (!user?.driver_id || !user?.token) {
        // No user info in localStorage/session
        return;
    }

    try {
        const result = await checkUserToken(user.driver_id, user.token);

        // Check if token is missing in database
        if (!result?.token) {
            alert('Your session has expired. Please log in again.');
            localStorage.removeItem('user');
            navigate('/');
        }
        // Optional: match check (only if DB token is not empty)
        else if (result.token !== user.token) {
            alert('Your session has expired. Please log in again.');
            localStorage.removeItem('user');
            navigate('/');
        }
    } catch (error) {
        // Only show alert if this is a real API/network failure, not on first load
        console.error('Token validation failed:', error);
    }
};

useEffect(() => {
  // Recompute isExpired every second for the selected job using any supported formats.
  if (!selectedJob) {
    setIsExpired(false);
    return;
  }

  const raw = selectedJob.bidExpiryAt || selectedJob.bid_expiry_time || '';
  
  const parseExpiry = () => {
    // Try ISO first
    let dt = DateTime.fromISO(String(raw), { zone: 'Europe/London' });
    if (dt.isValid) return dt;

    // Try common DB format: 2025-11-12 14:00:00
    dt = DateTime.fromFormat(String(raw), 'yyyy-MM-dd HH:mm:ss', { zone: 'Europe/London' });
    if (dt.isValid) return dt;

    // Try RFC-like (no T) fallback
    dt = DateTime.fromSQL(String(raw), { zone: 'Europe/London' });
    if (dt.isValid) return dt;

    // Try relative "1h 2m 3s"
    const rel = String(raw).match(/(\d+)\s*h\s*(\d+)\s*m\s*(\d+)\s*s/i);
    if (rel) {
      const [, h, m, s] = rel;
      return DateTime.now().setZone('Europe/London').plus(Duration.fromObject({
        hours: Number(h), minutes: Number(m), seconds: Number(s)
      }));
    }

    // Try plain time "HH:mm:ss" (assume today)
    const timeOnly = String(raw).match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
    if (timeOnly) {
      const [, hh, mm, ss] = timeOnly;
      const today = DateTime.now().setZone('Europe/London').toFormat('yyyy-MM-dd');
      return DateTime.fromFormat(`${today} ${hh}:${mm}:${ss ?? '00'}`, 'yyyy-MM-dd HH:mm:ss', { zone: 'Europe/London' });
    }

    return null;
  };

  const expiryDt = parseExpiry();

  if (!expiryDt || !expiryDt.isValid) {
    // Can't parse — treat as not expired (button enabled) and stop
    setIsExpired(false);
    return;
  }

  const check = () => setIsExpired(DateTime.now().setZone('Europe/London') >= expiryDt);

  // initial check + interval
  check();
  const interval = setInterval(check, 1000);
  return () => clearInterval(interval);
}, [selectedJob?.bidExpiryAt, selectedJob?.bid_expiry_time]);

useEffect(() => {
    const timer = setTimeout(() => {
        validateUserToken();  // 🔒 run after slight delay
    }, 2000); // wait 2 seconds after mount

    return () => clearTimeout(timer);
}, []);
    const [cars, setCars] = useState([]);
    const clearFilters = () => {
        setFilters({
            booking_ref_id: '',
            from_address: '',
            to_address: '',
            waypoint: '',
            pickup_date: '',
            passengers: '',
            luggage: '',
            distance: '',
            car_id: [],
            bid_expiry: '',
        });
    };
    const tabCounts = {
        available: filteredJobs.length, // Quotation tab
    };
    const fetchJobs = async () => {
        if (!user?.driver_id || !user?.token) return;
        setLoading(true);
        try {
            const params = {
                page,
                per_page: perPage,
                ref_filter: filters.booking_ref_id,
                date: filters.pickup_date,
                vehicle: filters.car_id,
                from_address: filters.from_address,
                to_address: filters.to_address
            };

            const response = await fetchAvailableJobs(user.driver_id, user.token, params);

            if (response?.data) {
                // compute a fixed expiry timestamp for each job so countdown can decrement
                const jobsWithExpiry = (response.data.data || []).map(job => {
                  const raw = job.bid_expiry_time ?? job.bidExpiryAt ?? '';
                  const bidExpiryAt = toExpiryISO(raw);
                  return { ...job, bidExpiryAt };
                });

                setJobs(jobsWithExpiry);
                setFilteredJobs(jobsWithExpiry);
                setPagination(response.data.pagination || null);
            }

            const carsList = await getAllCars(user.driver_id, user.token);
            setCars(carsList || []);
        } catch (err) {
            console.error("Error fetching available jobs:", err);
        } finally {
            setLoading(false);
        }
    };

    // Trigger fetch on filters/page change
    useEffect(() => {
        fetchJobs();
    }, [page, filters]);

    const userVehicleIds = user.vehicle_id.split(',').map(id => id.trim());
    const filteredCars = cars.filter(car => userVehicleIds.includes(car.car_id));
    const vehicleOptions = filteredCars.map(car => ({
        label: car.car_name,
        value: car.car_id,
    }));
    const getCarName = (car_id) => {
        const car = cars.find((c) => c.car_id === car_id);
        return car ? car.car_name : car_id;
    };

    useEffect(() => {
        const filtered = jobs.filter((job) => {
            const matchText = (key) =>
                job[key]?.toString().toLowerCase().includes(filters[key].toLowerCase());

            const matchPostcode = (key) => {
                const input = filters[key]?.toLowerCase().trim();
                if (!input) return true;

                const jobValue = job[key]?.toString().toLowerCase();
                const postcodeMatch = jobValue.match(/[A-Z]{1,2}\d{1,2}[A-Z]?\s*\d[A-Z]{2}/i);

                if (postcodeMatch) {
                    const fullPostcode = postcodeMatch[0].replace(/\s+/g, '').toLowerCase();
                    const prefix = fullPostcode.slice(0, input.length);
                    return prefix === input;
                }

                return false;
            };

            // Format pickup_date
            let jobDateISO = '';
            if (job.pickup_date) {
                const dt = DateTime.fromFormat(job.pickup_date, "cccc, dd LLL yyyy 'at' HH:mm", { zone: 'Europe/London' });
                jobDateISO = dt.isValid ? dt.toISODate() : '';
            }

            const dateMatch = !filters.pickup_date || jobDateISO === filters.pickup_date;
            const carMatch = !filters.car_id || filters.car_id.length === 0 || filters.car_id.includes(job.car_id);
            const fromPostcodeMatch = matchPostcode('from_address');
            const toPostcodeMatch = matchPostcode('to_address');
            const bookingRefMatch = !filters.booking_ref_id || matchText('booking_ref_id');

            return fromPostcodeMatch && toPostcodeMatch && dateMatch && carMatch && bookingRefMatch;
        });

        setFilteredJobs(filtered);
    }, [filters, jobs]);


    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({
            ...prev,
            [name]: value
        }));
        setPage(1); // Reset to first page on filter change
    };



    const handleViewDetails = async (job) => {
        setDisabledButton(prev => new Set(prev).add(job.booking_journey_id));

        setShowModal(true);
        setSelectedJob(job);
        setIsChecked(true);
        setLoadingDetails(true);
        setDetailsError('');

        try {
            const details = await fetchJourneyDetails(job.booking_journey_id, user.driver_id, user.token);
            const detailObj = details[0] || {};

            // compute a normalized ISO expiry for details (prefer detail -> parent job)
            const raw = detailObj.bid_expiry_time ?? detailObj.bidExpiryAt ?? job.bid_expiry_time ?? job.bidExpiryAt ?? '';
            const bidExpiryAt = toExpiryISO(raw);

            setSelectedJob(prev => ({ ...prev, ...detailObj, bidExpiryAt }));
            refreshCounts();
        } catch (err) {
            setDetailsError('Failed to load details.');
        } finally {
            setDisabledButton(prev => {
                const newSet = new Set(prev);
                newSet.delete(job.booking_journey_id);
                return newSet;
            });
        }

        setLoadingDetails(false);
    };

    const handleSubmitBid = async () => {
  if (!selectedJob || !quote || !isChecked) return;

  setSubmitting(true);
  setDisabledButton(prev => new Set(prev).add(selectedJob.booking_journey_id));

  try {
    if (!user?.driver_id || !user?.token) {
      alert("User not authenticated.");
      return;
    }

    // ✅ Step 1: Fetch expiry time from backend
    

    // ✅ Step 2: Check if job already assigned
    const checkResult = await checkBidJobs(selectedJob.booking_journey_id, user.token);
    if (checkResult && (checkResult.assigned === true || checkResult.assigned === 1)) {
      alert("This job has already been assigned to another driver.");
      setShowModal(false);
      setQuote('');
      setIsChecked(false);
      setReaction(true);
      return;
    }

    // ✅ Step 3: Submit the bid
    await bidJob({
      booking_journey_id: selectedJob.booking_journey_id,
      driver_id: user.driver_id,
      email: user.email,
      fare: quote,
      token: user.token,
    });

    // ✅ Optimistic UI update
    setJobs(prev => prev.filter(j => j.booking_journey_id !== selectedJob.booking_journey_id));
    setFilteredJobs(prev => prev.filter(j => j.booking_journey_id !== selectedJob.booking_journey_id));

    refreshCounts();
    await fetchJobs();

    alert("Bid submitted successfully!");
    setShowModal(false);
    setQuote('');
    setIsChecked(false);
    setReaction(true);

  } catch (err) {
    alert("Failed to submit bid: " + err.message);
  } finally {
    setSubmitting(false);
    setDisabledButton(prev => {
      const newSet = new Set(prev);
      newSet.delete(selectedJob.booking_journey_id);
      return newSet;
    });
  }
};



    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedJob(null);
        setDetailsError('');
        setQuote('');
        setIsChecked(false);
    };

    const handleLogout = () => {
        localStorage.removeItem('user');
        navigate('/');
    };

    const [now, setNow] = useState(DateTime.now().setZone("Europe/London"));

  useEffect(() => {
    const interval = setInterval(() => {
      setNow(DateTime.now().setZone("Europe/London"));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

const ZONE = 'Europe/London';

  // normalize any incoming expiry value into an ISO timestamp (or null)
  const toExpiryISO = (raw) => {
    if (!raw && raw !== 0) return null;

    // Handle "BID EXPIRED" string — return a timestamp in the past
    if (String(raw).trim().toUpperCase() === 'BID EXPIRED') {
      return DateTime.now().setZone(ZONE).minus({ days: 1 }).toISO();
    }

    // already an ISO-like string
    if (typeof raw === 'string' && /\d{4}-\d{2}-\d{2}T/.test(raw)) {
      const dt = DateTime.fromISO(raw, { zone: ZONE });
      return dt.isValid ? dt.toISO() : null;
    }

    // DB style yyyy-MM-dd HH:mm:ss
    if (typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}$/.test(raw)) {
      const dt = DateTime.fromFormat(raw, 'yyyy-MM-dd HH:mm:ss', { zone: ZONE });
      return dt.isValid ? dt.toISO() : null;
    }

    // plain SQL / RFC
    if (typeof raw === 'string') {
      const dtSql = DateTime.fromSQL(raw, { zone: ZONE });
      if (dtSql.isValid) return dtSql.toISO();
    }

    // relative like "1h 2m 3s" (allow missing parts)
    const rel = String(raw).match(/(?:(\d+)\s*h)?\s*(?:(\d+)\s*m)?\s*(?:(\d+)\s*s)?/i);
    if (rel) {
      const h = Number(rel[1] || 0);
      const m = Number(rel[2] || 0);
      const s = Number(rel[3] || 0);
      if (h + m + s > 0) {
        return DateTime.now().setZone(ZONE).plus({ hours: h, minutes: m, seconds: s }).toISO();
      }
    }

    // time-only "HH:mm" or "HH:mm:ss" (assume today)
    const timeOnly = String(raw).match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
    if (timeOnly) {
      const [, hh, mm, ss] = timeOnly;
      const today = DateTime.now().setZone(ZONE).toFormat('yyyy-MM-dd');
      const dt = DateTime.fromFormat(`${today} ${hh}:${mm}:${ss || '00'}`, 'yyyy-MM-dd HH:mm:ss', { zone: ZONE });
      if (dt.isValid) return dt.toISO();
    }

    // numeric seconds (e.g. "600")
    if (/^\d+$/.test(String(raw).trim())) {
      return DateTime.now().setZone(ZONE).plus({ seconds: Number(String(raw).trim()) }).toISO();
    }

    return null;
  };

  // use centralized parsing for display — expect an ISO (preferred) or try to normalize
  const bidExpireTime = (bidExpiryAtOrRaw) => {
    if (!bidExpiryAtOrRaw) return <span className="text-gray-500">N/A</span>;

    // if not ISO, try to convert
    let iso = bidExpiryAtOrRaw;
    if (typeof iso !== 'string' || !/\d{4}-\d{2}-\d{2}T/.test(iso)) {
      iso = toExpiryISO(bidExpiryAtOrRaw);
    }
    if (!iso) return <span className="text-gray-500">Invalid format</span>;

    const expiryDt = DateTime.fromISO(iso, { zone: ZONE });
    if (!expiryDt.isValid) return <span className="text-gray-500">Invalid format</span>;

    const diff = expiryDt.diff(now, ['hours', 'minutes', 'seconds']).toObject();

    if ((diff.hours ?? 0) < 0 || (diff.minutes ?? 0) < 0 || (diff.seconds ?? 0) < 0) {
      return <span className="text-red-500">Expired</span>;
    }

    const hoursLeft = String(Math.floor(diff.hours ?? 0)).padStart(2, '0');
    const minutesLeft = String(Math.floor(diff.minutes ?? 0)).padStart(2, '0');
    const secondsLeft = String(Math.floor(diff.seconds ?? 0)).padStart(2, '0');

    return <span className="text-green-500 font-bold">{`${hoursLeft}:${minutesLeft}:${secondsLeft}`}</span>;
  };

  // Recompute isExpired for modal using normalized ISO
  useEffect(() => {
    if (!selectedJob) {
      setIsExpired(false);
      return;
    }
    const iso = toExpiryISO(selectedJob.bidExpiryAt ?? selectedJob.bid_expiry_time ?? '');
    if (!iso) {
      setIsExpired(false);
      return;
    }
    const expiryDt = DateTime.fromISO(iso, { zone: ZONE });
    if (!expiryDt.isValid) {
      setIsExpired(false);
      return;
    }

    const check = () => setIsExpired(DateTime.now().setZone(ZONE) >= expiryDt);
    check();
    const interval = setInterval(check, 1000);
    return () => clearInterval(interval);
  }, [selectedJob?.bidExpiryAt, selectedJob?.bid_expiry_time]);

    return (
        <>
            <Header />
            {actionLoading && (
                <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50">
                    <div className="bg-white p-4 rounded-lg shadow-lg">
                        <Loading />
                    </div>
                </div>
            )}
            <div className="dashboard-layout mx-5 mt-5">
                <div className={`dashboard-main${sidebarOpen ? '' : ' centered'}`}>
                    <div className="w-full">
                        <div className="w-full">
                            <JobsTabs activeTab="available" user={user} />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 px-5 mb-2">
                            <input
                                type="text"
                                name="booking_ref_id"
                                value={filters.booking_ref_id}
                                onChange={handleFilterChange}
                                placeholder="Booking Ref"
                                className="p-2 border border-gray-300 rounded-md w-full"
                            />
                            <input
                                type="text"
                                name="from_address"
                                value={filters.from_address}
                                onChange={handleFilterChange}
                                placeholder="From Address"
                                className="p-2 border border-gray-300 rounded-md w-full"
                            />
                            <input
                                type="text"
                                name="to_address"
                                value={filters.to_address}
                                onChange={handleFilterChange}
                                placeholder="To Address"
                                className="p-2 border border-gray-300 rounded-md w-full"
                            />
                            <input
                                type="date"
                                name="pickup_date"
                                value={filters.pickup_date}
                                onChange={handleFilterChange}
                                min={new Date().toISOString().split('T')[0]}
                                placeholder="Journey Date"
                                className="p-2 border border-gray-300 rounded-md w-full"
                            />

                            <Select
                                isMulti
                                name="car_id"
                                options={vehicleOptions}
                                value={vehicleOptions.filter(opt => filters.car_id.includes(opt.value))}
                                onChange={selectedOptions => {
                                    const selectedValues = selectedOptions.map(option => option.value);
                                    setFilters(prev => ({ ...prev, car_id: selectedValues }));
                                }}
                                className="w-full"
                                placeholder="Select Vehicle(s)"
                            />

                            <input
                                type="button"
                                value="Clear"
                                onClick={clearFilters}
                                className="p-2 border border-gray-300 rounded-md w-full cursor-pointer text-center bg-gray-100 hover:bg-red-100 text-red-500 font-semibold"
                            />
                        </div>

                        {loading ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
                                {Array.from({ length: 6 }).map((_, i) => (
                                    <QuotationCardSkeleton key={i} />
                                ))}
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
                                {filteredJobs.length > 0 ? (
                                    filteredJobs.map((job, idx) => {
                                        // Danger logic: 2 hours or less and no sub assigned
                                        let isDanger = false;
                                        let noSubAssigned = false;
                                        let alertMsg = null;
                                        if (job.pickup_date) {
                                            const journeyDate = DateTime.fromFormat(
                                                job.pickup_date,
                                                "cccc, dd LLL yyyy 'at' HH:mm",
                                                { zone: 'Europe/London' }
                                            );
                                            const now = DateTime.now().setZone('Europe/London');
                                            const diffInHours = journeyDate.diff(now, 'hours').hours;
                                            isDanger = diffInHours <= 2;
                                            noSubAssigned = !job.sub_assigned;
                                            if (isDanger && noSubAssigned && (user.user_type == 'supplier')) {
                                                alertMsg = (
                                                    <span className="mr-2 flex items-center text-xs text-red-600 font-bold">
                                                        ⚠️ Alert: Supplier has not assigned a sub for this job.
                                                    </span>
                                                );
                                            }
                                        }
                                        return (
                                            <div
                                                key={job.booking_id || idx}
                                                className={`bg-white rounded-xl p-4 flex flex-col h-full 
                                                ${isDanger && noSubAssigned && (user.user_type == 'supplier')
                                                        ? 'border-2 border-red-400 shadow-[0_0_10px_rgba(239,68,68,0.6)]'
                                                        : 'shadow-md'
                                                    }`}
                                            >
                                                {/* Top section: Alert and header */}
                                                <div className="flex justify-between items-center mb-2">
                                                    <div className="flex items-center">{alertMsg}</div>
                                                    <span className="text-sm text-blue-600 font-medium ml-auto"><b>Quotation</b></span>
                                                </div>

                                                {/* Middle content that fills vertically */}
                                                <div className="flex-1 flex flex-col">
                                                    <div className="mb-3">
                                                        <h4 className="text-base font-medium text-blue-600 flex items-center gap-2">
                                                            <i className="fas fa-car-side"></i> <b>{getCarName(job.car_id)}</b>
                                                        </h4>
                                                        <p className="text-sm text-gray-700 mt-1 flex flex-col sm:flex-row sm:items-center sm:justify-between">
                                                            <span className="flex items-center gap-2">
                                                                <i className="fas fa-receipt text-gray-500"></i> <b>{job.booking_ref_id}</b>
                                                            </span>
                                                            <span className="flex items-center gap-2">
                                                                <i className="fas fa-clock text-gray-500"></i>
                                                                <b className={job.manual_message ? "text-red-500" : ""}>
                                                                    {job.manual_message ? job.manual_message : bidExpireTime(job.bidExpiryAt || job.bid_expiry_time)}
                                                                </b>
                                                            </span>
                                                        </p>
                                                    </div>

                                                    <div className="space-y-1 text-sm text-gray-700">
                                                        {/* Pickup */}
                                                        <div>
                                                            <span className="flex items-center gap-2 font-medium text-gray-600">
                                                                <i className="fas fa-map-marker-alt text-blue-500"></i> <b>Pickup:</b>
                                                            </span>
                                                            <span className="block ml-6"><b>{job.from_address}</b></span>
                                                        </div>

                                                        {/* Waypoints */}
                                                        {job.waypoint?.trim() !== '' &&
                                                            job.waypoint.split('|').map((wp, i) =>
                                                                wp.trim() && (
                                                                    <div key={i}>
                                                                        <span className="flex items-center gap-2 font-medium text-gray-600">
                                                                            <i className="fas fa-map-marker-alt text-blue-500"></i>
                                                                            <b>Waypoint{job.waypoint.split('|').length > 1 ? ` ${i + 1}` : ''}:</b>
                                                                        </span>
                                                                        <span className="block ml-6"><b>{wp.trim()}</b></span>
                                                                    </div>
                                                                )
                                                            )}

                                                        {/* DropOff */}
                                                        <div>
                                                            <span className="flex items-center gap-2 font-medium text-gray-600">
                                                                <i className="fas fa-map-pin text-red-500"></i> <b>DropOff:</b>
                                                            </span>
                                                            <span className="block ml-6"><b>{job.to_address}</b></span>
                                                        </div>

                                                        {/* Other Info */}
                                                        <div className="flex justify-between">
                                                            <span className="flex items-center gap-2 font-medium text-gray-600">
                                                                <i className="fas fa-road text-yellow-500"></i> <b>Distance:</b>
                                                            </span>
                                                            <span className="text-right"><b>{job.distance} Approx</b></span>
                                                        </div>

                                                        <div className="flex justify-between">
                                                            <span className="flex items-center gap-2 font-medium text-gray-600">
                                                                <i className="fas fa-calendar-alt text-blue-400"></i> <b>Journey Date:</b>
                                                            </span>
                                                            <span className="text-right"><b>{job.pickup_date?.split(' at ')[0]}</b></span>
                                                        </div>

                                                        <div className="flex justify-between">
                                                            <span className="flex items-center gap-2 font-medium text-gray-600">
                                                                <i className="fas fa-clock text-purple-500"></i> <b>Journey Time:</b>
                                                            </span>
                                                            <span className="text-right">
                                                                <b>{job.pickup_date
                                                                    ? DateTime.fromFormat(job.pickup_date, "cccc, dd LLL yyyy 'at' HH:mm", { zone: 'Europe/London' }).toFormat("hh:mm a")
                                                                    : ''}</b>
                                                            </span>
                                                        </div>

                                                        <div className="flex justify-between">
                                                            <span className="flex items-center gap-2 font-medium text-gray-600">
                                                                <i className="fas fa-users text-purple-500"></i> <b>Passengers:</b>
                                                            </span>
                                                            <span className="text-right"><b>{job.passengers}</b></span>
                                                        </div>

                                                        <div className="flex justify-between">
                                                            <span className="flex items-center gap-2 font-medium text-gray-600">
                                                                <i className="fas fa-suitcase text-pink-500"></i> <b>Luggage:</b>
                                                            </span>
                                                            <span className="text-right"><b>{job.luggage}</b></span>
                                                        </div>

                                                        {job.flight_no?.trim() !== '' && (
                                                            <div className="flex justify-between">
                                                                <span className="flex items-center gap-2 font-medium text-gray-600">
                                                                    <i className="fas fa-plane text-indigo-500"></i> <b>Flight No:</b>
                                                                </span>
                                                                <span className="text-right"><b>{job.flight_no}</b></span>
                                                            </div>
                                                        )}

                                                        {job.arrive_from?.trim() !== '' && (
                                                            <div className="flex justify-between">
                                                                <span className="flex items-center gap-2 font-medium text-gray-600">
                                                                    <i className="fas fa-globe-europe text-teal-500"></i> <b>Arrive From:</b>
                                                                </span>
                                                                <span className="text-right"><b>{job.arrive_from}</b></span>
                                                            </div>
                                                        )}

                                                        <div className="flex justify-between">
                                                            <span className="flex items-center gap-2 font-medium text-gray-600">
                                                                <i className="fas fa-handshake text-green-500"></i> <b>Meet & Greet:</b>
                                                            </span>
                                                            <span className="text-right"><b>{job.meet_greet === 1 || job.meet_greet === '1' ? 'Yes' : 'No'}</b></span>
                                                        </div>

                                                        {job.driver_supplier_remarks?.trim() !== '' && (
                                                            <div>
                                                                <span className="flex items-center gap-2 font-medium text-gray-600">
                                                                    <i className="fas fa-id-card text-blue-500"></i><b> Driver Instructions:</b>
                                                                </span>
                                                                <div className="ml-6" dangerouslySetInnerHTML={{ __html: job.driver_supplier_remarks }} />
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Bottom section pinned */}
                                                <div className="mt-auto pt-3 flex flex-col space-y-3">
                                                    {job.car_info && (
                                                        <div className="text-xs text-gray-500 border-t pt-2">
                                                            <p className="text-sm text-gray-700 mt-1 flex items-center gap-2">
                                                                <i className="fas fa-info-circle text-blue-500"></i> {job.car_info}
                                                            </p>
                                                        </div>
                                                    )}
                                                    <button
                                                        onClick={() => handleViewDetails(job)}
                                                        className="sm:flex-1 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium py-2 rounded w-full"
                                                        disabled={disabledButton.has(job.booking_journey_id)}
                                                    >
                                                        View Details
                                                    </button>
                                                </div>
                                            </div>

                                        );
                                    })
                                ) : (
                                    <div className="col-span-full text-center text-gray-600 p-4 border rounded-md">
                                        <p>No available jobs matching your criteria.</p>
                                    </div>
                                )}
                            </div>
                        )}

                    </div>
                    {pagination && (
                        <div className="flex justify-center gap-4 my-4">
                            <button
                                disabled={pagination.current_page === 1}
                                onClick={() => setPage(p => p - 1)}
                                className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
                            >
                                Previous
                            </button>
                            <span>Page {pagination.current_page} of {pagination.total_pages}</span>
                            <button
                                disabled={pagination.current_page === pagination.total_pages}
                                onClick={() => setPage(p => p + 1)}
                                className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
                            >
                                Next
                            </button>
                        </div>
                    )}
                    {showModal && selectedJob && (
                        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                            <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full relative">
                                <button
                                    onClick={handleCloseModal}
                                    className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
                                    aria-label="Close"
                                >
                                    &times;
                                </button>
                                {loadingDetails ? (
                                    <div className="flex flex-col items-center justify-center h-40">
                                        <Loading />
                                        <span className="mt-4 text-gray-600">Loading details...</span>
                                    </div>
                                ) : detailsError ? (
                                    <div className="text-red-600 text-center">{detailsError}</div>
                                ) : (
                                    <>
                                        <div className="flex justify-between items-center mb-4">
                                            <h2 className="text-xl font-bold text-gray-800">Submit Your Quote</h2>
                                            <span className="text-green-600 font-bold">
                                                Guide Price: £{selectedJob.guidedprice ?? 'N/A'}
                                            </span>
                                        </div>
                                        <div className="mb-4">
                                            <div className="flex items-center mb-2">
                                                <i className="fas fa-calendar-alt mr-2 text-blue-600"></i>
                                                <span className="font-medium text-gray-700">
                                                    Bid Expiry: {selectedJob.bid_expiry_date ?? 'N/A'}
                                                </span>
                                            </div>
                                            <div className="flex items-center">
                                                <i className="fas fa-clock mr-2 text-blue-600"></i>
                                                <span className="font-medium text-gray-700">
                                                Bid Expire Time:{' '}
                                                <span className={selectedJob.manual_message ? 'text-red-500' : 'text-gray-700'}>
                                                    {selectedJob.manual_message
                                                    ? selectedJob.manual_message
                                                    : bidExpireTime(selectedJob.bidExpiryAt || selectedJob.bid_expiry_time) ?? 'N/A'}
                                                </span>
                                                </span>
                                            </div>
                                        </div>
                                        <div className="mb-4">
                                            <input
                                                type="number"
                                                placeholder="£ Quote Here"
                                                value={quote}
                                                onChange={(e) => setQuote(e.target.value.replace(/[^0-9.]/g, ''))}
                                                className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                        </div>
                                        <div className="mt-4 mb-2 flex items-center">
                                            <input
                                                type="checkbox"
                                                checked={isChecked}
                                                onChange={(e) => setIsChecked(e.target.checked)}
                                                className="mr-2"
                                            />
                                            <span className="text-gray-700">
                                                By submitting your quote, you are accepting the Jewels Airport Transfers{' '}
                                                <a href="https://jat-uk.com/instructions-and-terms" target="_blank" rel="noopener noreferrer" className="text-blue-500">
                                                    terms and conditions
                                                </a>
                                            </span>
                                        </div>
                                        <button
                                            className={`w-full p-3 rounded text-white font-medium ${
                                                !isChecked || !quote || disabledButton.has(selectedJob.booking_journey_id) || isExpired
                                                ? 'bg-gray-400 cursor-not-allowed'
                                                : 'bg-blue-600 hover:bg-blue-700'
                                            }`}
                                            disabled={!isChecked || !quote || disabledButton.has(selectedJob.booking_journey_id) || isExpired}
                                            onClick={handleSubmitBid}
                                            >
                                            {isExpired
                                                ? 'Expired'
                                                : submitting
                                                ? 'Submitting...'
                                                : 'Submit'}
                                            </button>
                                    </>
                                )}
                            </div>
                        </div>
                    )}

                </div>
            </div>
        </>
    );
}